import { StandardEvents } from '@shopify/events';

/**
 * Collection tabs with visibility-aware auto rotation and compact product-card controls.
 * The component deliberately relies on Horizon's product-form-component for cart mutations.
 */
class SkFeaturedCollections extends HTMLElement {
  #tabs = [];
  #panels = [];
  #activeIndex = 0;
  #timer = 0;
  #isVisible = false;
  #userPaused = false;
  #observer;
  #abortController = new AbortController();
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.#panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    this.#activeIndex = Math.max(
      0,
      this.#tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );

    const { signal } = this.#abortController;
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.addEventListener('pointerover', this.#handleMediaIntent, { signal });
    this.addEventListener('focusin', this.#handleMediaIntent, { signal });
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });
    document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });

    this.#observer = new IntersectionObserver(this.#handleIntersection, {
      threshold: [0, 0.15],
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#stopRotation();
    this.#observer?.disconnect();
    this.#abortController.abort();
  }

  #handleIntersection = ([entry]) => {
    const wasVisible = this.#isVisible;
    this.#isVisible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.15);

    if (!this.#isVisible) {
      this.#stopRotation();
      if (wasVisible) this.#userPaused = false;
      return;
    }

    this.#startRotation();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#stopRotation();
    } else {
      this.#startRotation();
    }
  };

  #startRotation() {
    this.#stopRotation();
    if (
      !this.#isVisible ||
      this.#userPaused ||
      document.hidden ||
      this.dataset.autoRotate !== 'true' ||
      this.#reducedMotion.matches ||
      this.#tabs.length < 2
    ) {
      return;
    }

    const interval = Math.max(2500, Number(this.dataset.interval) || 5000);
    this.#timer = window.setInterval(() => {
      this.#selectTab((this.#activeIndex + 1) % this.#tabs.length, false);
    }, interval);
  }

  #stopRotation() {
    if (!this.#timer) return;
    window.clearInterval(this.#timer);
    this.#timer = 0;
  }

  #pauseForUser() {
    this.#userPaused = true;
    this.#stopRotation();
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[role="tab"]');
    if (tab && this.contains(tab)) {
      const index = this.#tabs.indexOf(tab);
      if (index >= 0) {
        this.#pauseForUser();
        this.#selectTab(index, true);
      }
      return;
    }

    const sizeToggle = event.target.closest('[data-size-toggle]');
    if (sizeToggle && this.contains(sizeToggle)) {
      this.#pauseForUser();
      this.#toggleSizes(sizeToggle);
      return;
    }

    const sizeChoice = event.target.closest('[data-size-choice]');
    if (sizeChoice && this.contains(sizeChoice)) {
      this.#pauseForUser();
      this.#selectSize(sizeChoice);
      return;
    }

    if (event.target.closest('button, a, input, select') && event.target.closest('[data-product-card]')) {
      this.#pauseForUser();
    }
  };

  #handleKeydown = (event) => {
    if (!(event.target instanceof Element)) return;
    const tab = event.target.closest('[role="tab"]');
    if (!tab) return;

    const currentIndex = this.#tabs.indexOf(tab);
    if (currentIndex < 0) return;

    let nextIndex;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % this.#tabs.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + this.#tabs.length) % this.#tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = this.#tabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    this.#pauseForUser();
    this.#selectTab(nextIndex, true);
    this.#tabs[nextIndex]?.focus();
  };

  #handleMediaIntent = (event) => {
    if (!(event.target instanceof Element)) return;
    const card = event.target.closest('[data-product-card]');
    if (card && this.contains(card)) this.#loadSecondaryImage(card);
  };

  #loadSecondaryImage(card) {
    const image = card.querySelector('[data-secondary-image]');
    if (!(image instanceof HTMLImageElement) || !image.dataset.src) return;

    const reveal = () => card.classList.add('has-secondary-image');
    image.addEventListener('load', reveal, { once: true });
    if (image.dataset.srcset) image.srcset = image.dataset.srcset;
    image.src = image.dataset.src;
    delete image.dataset.src;
    delete image.dataset.srcset;

    if (image.complete && image.naturalWidth > 80) reveal();
  }

  #selectTab(index, userInitiated) {
    const nextTab = this.#tabs[index];
    const nextPanel = this.#panels[index];
    if (!nextTab || !nextPanel) return;

    this.#tabs.forEach((tab, tabIndex) => {
      const isActive = tabIndex === index;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
      tab.classList.toggle('is-active', isActive);
    });

    this.#panels.forEach((panel, panelIndex) => {
      const isActive = panelIndex === index;
      panel.hidden = !isActive;
      panel.classList.toggle('is-active', isActive);
    });

    this.#activeIndex = index;
    const viewAll = this.querySelector('[data-view-all]');
    if (viewAll instanceof HTMLAnchorElement && nextPanel.dataset.collectionUrl) {
      viewAll.href = nextPanel.dataset.collectionUrl;
      viewAll.setAttribute('aria-label', nextPanel.dataset.viewAllLabel || viewAll.textContent.trim());
    }

    if (!this.#reducedMotion.matches) {
      nextPanel.classList.remove('is-entering');
      requestAnimationFrame(() => nextPanel.classList.add('is-entering'));
    }

    nextTab.scrollIntoView({
      behavior: this.#reducedMotion.matches || userInitiated ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }

  #toggleSizes(toggle) {
    const card = toggle.closest('[data-product-card]');
    const options = card?.querySelector('[data-size-options]');
    if (!(options instanceof HTMLElement)) return;

    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    options.hidden = expanded;
  }

  #selectSize(choice) {
    const card = choice.closest('[data-product-card]');
    if (!card) return;

    const variantId = choice.dataset.variantId;
    const input = card.querySelector('input[name="id"]');
    if (input instanceof HTMLInputElement && variantId) input.value = variantId;

    const quantity = card.querySelector('input[name="quantity"]');
    if (quantity instanceof HTMLInputElement && choice.dataset.minimumQuantity) {
      quantity.value = choice.dataset.minimumQuantity;
    }

    const productForm = card.querySelector('product-form-component');
    if (productForm instanceof HTMLElement && choice.dataset.minimumQuantity) {
      productForm.dataset.quantityDefault = choice.dataset.minimumQuantity;
    }

    card.querySelectorAll('[data-size-choice]').forEach((button) => {
      const selected = button === choice;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });

    const currentSize = card.querySelector('[data-current-size]');
    if (currentSize) currentSize.textContent = choice.dataset.sizeLabel || choice.textContent.trim();

    const price = card.querySelector('[data-current-price]');
    if (price && choice.dataset.price) price.textContent = choice.dataset.price;

    const comparePrice = card.querySelector('[data-compare-price]');
    if (comparePrice) {
      comparePrice.textContent = choice.dataset.comparePrice || '';
      comparePrice.toggleAttribute('hidden', !choice.dataset.comparePrice);
    }

    const addButton = card.querySelector('button[name="add"]');
    if (addButton instanceof HTMLButtonElement) addButton.disabled = choice.dataset.available !== 'true';

    const options = card.querySelector('[data-size-options]');
    const toggle = card.querySelector('[data-size-toggle]');
    if (options instanceof HTMLElement) options.hidden = true;
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  #handleCartLinesUpdate = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const source = event.target instanceof Element ? event.target : null;
    const button = source?.querySelector('button[name="add"]');
    if (button instanceof HTMLButtonElement) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    }

    const cartPromise = event.promise;
    if (!cartPromise) {
      if (button instanceof HTMLButtonElement) {
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }
      return;
    }

    cartPromise
      .then(({ detail }) => {
        if (detail?.didError) return;
        if (typeof window.refreshAndOpenKushaCartDrawer === 'function') {
          window.refreshAndOpenKushaCartDrawer();
          return;
        }
        const openCart = window.Shopify?.actions?.openCart;
        if (typeof openCart === 'function') {
          openCart();
          return;
        }

        const drawer = document.querySelector('theme-drawer#cart-drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open();
      })
      .catch(() => {})
      .finally(() => {
        if (button instanceof HTMLButtonElement) {
          button.removeAttribute('aria-busy');
          button.disabled = false;
        }
      });
  };

  #handleBlockSelect = (event) => {
    const blockId = event.detail?.blockId;
    if (!blockId) return;
    const tab = this.#tabs.find((candidate) => candidate.dataset.blockId === blockId);
    const index = tab ? this.#tabs.indexOf(tab) : -1;
    if (index < 0) return;

    this.#pauseForUser();
    this.#selectTab(index, true);
  };
}

if (!customElements.get('sk-featured-collections')) {
  customElements.define('sk-featured-collections', SkFeaturedCollections);
}
